<template>
  <BasePopover
    v-model="isCalendarOpen"
    :placement="popperOptions.placement"
    :padding="popperOptions.padding"
    :popper-class="[popperOptions.class, 'date-range-picker__popper']"
    :is-in-modal="isInModal"
    show-on="none"
    :close-on-click-reference="closeOnClickReference"
    :close-on-click-outside="closeOnClickOutside"
    offset="0"
  >
    <template #reference>
      <slot name="label" />
      <DateRangeField
        ref="reference"
        :label="label"
        :hide-input="hideInput"
        :min-date="minDate"
        :max-date="maxDate"
        :start-date="localStartDate"
        :end-date="localEndDate"
        :disabled="disabled"
        :date-delimiter="locale.delimiter"
        :range-key-mask="rangeKeyMask"
        :small="small"
        @click.native="openPicker"
        @input="updateRanges"
        @dateSubmitted="closePicker"
      />
    </template>
    <template #popper>
      <DropdownCalendar
        :two-calendars="twoCalendars"
        :min-date="minDate"
        :max-date="maxDate"
        :start-date="localStartDate"
        :end-date="localEndDate"
        :month-date="monthDate"
        :are-range-presets-hidden="areRangePresetsHidden"
        :small="small"
        :locale="locale"
        :ranges="ranges"
        :active-range-key="activeRangeKey"
        :hide-clear-button="hideClearButton"
        @close="closePicker"
        @clickRange="clickRange"
        @clear="clear"
        @changeMonth="changeMonth"
        @input="dateClickInCalendar"
      />
    </template>
  </BasePopover>
</template>

<script>
  import BasePopover from 'components/common/BasePopover';
  import dateRangePresets from 'components/Inputs/Dates/DateRangePicker/daterangepresets';
  import DateRangeField from 'components/Inputs/Dates/DateRangePicker/DateRangeField';
  import DropdownCalendar from 'components/Inputs/Dates/DateRangePicker/DropdownCalendar';

  import getTr from 'translation';
  import {
    isDayAfter,
    isDayBefore,
    isDayEqual,
    dateIsRelative,
    dateIsBetween,
    getUnixTimestamp
  } from 'components/Inputs/Dates/DateRangePicker/util';

  import {dateFormatter} from 'components/Inputs/Dates/utils/dateformatter';
  import {momentWrapper} from 'components/Inputs/Dates/utils/momentWrapper';

  import _ from 'lodash';
  _.noConflict();

  const API_DATE_FORMAT = dateFormatter.momentDateApiFormat();

  export default {
    name: 'DateRangePicker',
    components: {
      BasePopover,
      DateRangeField,
      DropdownCalendar
    },
    props: {
      startDate: {
        type: Date,
        default: null
      },
      endDate: {
        type: Date,
        default: null
      },
      minDate: {
        type: Date,
        default: null
      },
      maxDate: {
        type: Date,
        default: null
      },
      localeData: {
        type: Object,
        default: Object
      },
      label: {
        type: String,
        default: null
      },
      twoCalendars: {
        type: Boolean,
        default: false
      },
      customRanges: {
        type: Object,
        default: null
      },
      areRangePresetsHidden: {
        type: Boolean,
        default: false
      },
      isInModal: {
        type: Boolean,
        default: false
      },
      popperOptions: {
        type: Object,
        default: () => ({
          padding: '22px 24px 15px',
          placement: 'bottom-start',
          class: null
        })
      },
      small: {
        type: Boolean,
        default: true
      },
      disabled: {
        type: Boolean,
        default: false
      },

      // hides date input, only calendar icon is shown
      hideInput: {
        type: Boolean,
        default: false
      },

      // if true, emits date immediately (before click OK button)
      autoApply: {
        type: Boolean,
        default: false
      },

      // max number of days allowed to be selected in calendar
      maxRangeDuration: {
        type: Number,
        default: null
      },

      // shows range text in DateRangeInput instead of dates
      rangeKeyMask: {
        type: String,
        default: null
      },

      // set today date
      setDefaultDateAfterClear: {
        type: Boolean,
        default: false
      },

      // close dropdown calendar by clicking outside this dropdown
      closeOnClickOutside: {
        type: Boolean,
        default: true
      },

      // reference it's the input field with dates
      closeOnClickReference: {
        type: Boolean,
        default: false
      },
      hideClearButton: {
        type: Boolean,
        default: false
      }
    },
    data () {
      return {
        locale: {

          // default locale
          direction: 'ltr',
          format: 'DD.MM.YY',
          inputFormat: 'DD.MM.YY',
          delimiter: '.',
          separator: '-',
          applyLabel: 'OK',
          cancelLabel: getTr('cancel'),
          clearLabel: getTr('reset'),
          weekLabel: 'W',
          customRangeLabel: 'Custom Range',
          daysOfWeek: this.getWeekdays(),
          firstDay: momentWrapper().firstDayOfWeek(),
          ...this.localeData
        },
        localStartDate: null, // local start date (type Date), prevent changing prop startDate
        localEndDate: null, // local end date (type Date), prevent changing prop endDate
        monthDate: null, // date (type Date), which shows the month, which should be on the left in dropdown
        isCalendarOpen: false,
        activeRangeKey: null
      };
    },
    computed: {
      rangeDurationMilliseconds () {
        return getUnixTimestamp(this.localEndDate) - getUnixTimestamp(this.localStartDate);
      },
      maxRangeDurationMilliseconds () {
        const rangeDayForCalculate = this.maxRangeDuration ? this.maxRangeDuration - 1 : null;
        return momentWrapper.duration(rangeDayForCalculate, 'days').asMilliseconds();
      },
      restrictionsDiff () {
        if (this.maxRangeDuration === null || this.localEndDate === null || this.localStartDate === null) {
          return 0;
        }
        const diff = momentWrapper.duration(this.maxRangeDurationMilliseconds - this.rangeDurationMilliseconds);
        return diff < 0 ? -diff : 0;
      },
      ranges () {
        const ranges = {};

        if (this.areRangePresetsHidden) {
          return ranges;
        }

        if (this.customRanges) {
          return this.customRanges;
        }

        if (this.minDate && this.maxDate) {
          ranges['All time'] = [
            momentWrapper(this.minDate),
            momentWrapper(this.maxDate)
          ];
        }

        ranges.Yesterday = dateRangePresets.YESTERDAY;
        ranges['Last 7 days'] = dateRangePresets.LAST_7_DAYS;
        ranges['This week'] = dateRangePresets.THIS_WEEK;
        ranges['Last week'] = dateRangePresets.LAST_WEEK;
        ranges['This month'] = dateRangePresets.THIS_MONTH;
        ranges['Last month'] = dateRangePresets.LAST_MONTH;

        const processRangesByMinMaxRestriction = rangeKeys => rangeKeys.filter(key => {
          const [rangeStart, rangeEnd] = ranges[key];
          return !(
            this.minDate && isDayBefore(rangeEnd, this.minDate) ||
            this.maxDate && isDayAfter(rangeStart, this.maxDate)
          );
        });

        const filterRangesByDurationRestriction = rangeKeys => rangeKeys.filter(key => {
          const [rangeStart, rangeEnd] = ranges[key];
          return getUnixTimestamp(rangeEnd) - getUnixTimestamp(rangeStart) <= this.maxRangeDurationMilliseconds;
        });

        const normalizeRanges = rangeKeys => rangeKeys.reduce((all, key) => {
          let [start, end] = ranges[key];
          if (this.minDate && isDayBefore(start, this.minDate)) {
            start = this.minDate;
          }
          if (this.maxDate && isDayAfter(end, this.maxDate)) {
            end = this.maxDate;
          }

          all[key] = [this.normalizeDatetime(start), this.normalizeDatetime(end)];
          return all;
        }, {});

        const rangeKeys = Object.keys(ranges);

        const filteredRangeKeys = this.maxRangeDuration !== null
          ? processRangesByMinMaxRestriction(filterRangesByDurationRestriction(rangeKeys))
          : processRangesByMinMaxRestriction(rangeKeys);

        return normalizeRanges(filteredRangeKeys);
      }
    },
    watch: {
      startDate (date) {
        this.localStartDate = date;
        if (!dateIsRelative(date)) {
          this.activeRangeKey = null;
        }
      },
      endDate (date) {
        this.localEndDate = date;
        if (!dateIsRelative(date)) {
          this.activeRangeKey = null;
        }
      },
      minDate () {
        this.changeMonth(this.monthDate);
      },
      maxDate () {
        this.changeMonth(this.monthDate);
      }
    },
    created () {
      this.locale.apiFormat = API_DATE_FORMAT;

      if (this.startDate) {
        this.localStartDate = momentWrapper(this.startDate).toDate();
      }
      if (this.endDate) {
        this.localEndDate = momentWrapper(this.endDate).toDate();
      }

      this.monthDate = this.localStartDate ? this.localStartDate : new Date();

      // update day names order to firstDay
      if (this.locale.firstDay !== 0) {
        let iterator = this.locale.firstDay;
        while (iterator > 0) {
          this.locale.daysOfWeek.push(this.locale.daysOfWeek.shift());
          iterator -= 1;
        }
      }
    },
    methods: {
      updateRanges ({startDate, endDate}) {
        if (this.disabled) {
          return;
        }

        if (startDate) {
          this.setStart(startDate);
          this.changeMonth(startDate);
        }
        if (endDate) {
          this.setEnd(endDate);
        }
      },
      getWeekdays () {
        return _.range(7).map(day => momentWrapper().lang().
          weekdaysMin(momentWrapper().day(day % 7), 'nominative'));
      },
      changeMonth (newDate) {
        if (!this.maxDate || !this.minDate) {
          this.monthDate = newDate;
        } else if (dateIsBetween(newDate, this.minDate, this.maxDate)) {
          this.monthDate = newDate;
        } else if (momentWrapper(newDate).isAfter(this.maxDate) || momentWrapper(newDate).isSame(this.maxDate)) {
          this.monthDate = this.maxDate;
        } else {
          this.monthDate = this.minDate;
        }
      },
      normalizeDatetime (value, oldValue) {
        const newDate = momentWrapper(value).toDate();
        if (!oldValue) {

          // time = 00:00:00:000
          oldValue = momentWrapper().startOf('day').
            toDate();
        }

        newDate.setHours(oldValue.getHours());
        newDate.setMinutes(oldValue.getMinutes());
        newDate.setSeconds(oldValue.getSeconds());
        newDate.setMilliseconds(oldValue.getMilliseconds());

        return newDate;
      },
      dateClickInCalendar ({value, order}) {
        if (this.twoCalendars) {
          if (order === 'start') {
            this.setStart(value);
          }
          if (order === 'end') {
            this.setEnd(value);
          }
          return;
        }

        /*
         * Still both ends must be set or none of them
         * First click sets 1-day interval
         * Second click modifies interval
         * Third click resets interval and starts it all over
         */
        if (isDayEqual(this.localStartDate, this.localEndDate)) {
          if (isDayBefore(value, this.localStartDate)) {
            this.setStart(value);
          } else if (isDayAfter(value, this.localEndDate)) {
            this.setEnd(value);
          }
        } else {
          this.setStartAndEnd(value);
        }

        if (this.autoApply) {
          this.clickedApply();
        }
      },
      fitDateToRange (date) {
        if (this.minDate && date < this.minDate) {
          return this.minDate;
        }
        if (this.maxDate && date > this.maxDate) {
          return this.maxDate;
        }
        return date;
      },
      setStartAndEnd (value) {
        const fitValue = this.fitDateToRange(value);
        this.localStartDate = this.normalizeDatetime(fitValue, this.localStartDate);
        this.localEndDate = this.normalizeDatetime(fitValue, this.localEndDate);
        this.activeRangeKey = null;
      },
      setStart (value) {
        const fitValue = this.fitDateToRange(value);
        this.localStartDate = this.normalizeDatetime(fitValue, this.localStartDate);
        this.localStartDate = this.normalizeDatetime(getUnixTimestamp(this.localStartDate) +
          this.restrictionsDiff, this.localStartDate);
        this.activeRangeKey = null;
      },
      setEnd (value) {
        const fitValue = this.fitDateToRange(value);
        this.localEndDate = this.normalizeDatetime(fitValue, this.localEndDate);
        this.localEndDate = this.normalizeDatetime(getUnixTimestamp(this.localEndDate) -
          this.restrictionsDiff, this.localEndDate);
        this.activeRangeKey = null;
      },
      togglePickerDropdown () {
        if (this.disabled) {
          this.isCalendarOpen = false;

        }
      },
      openPicker () {
        if (this.disabled) {
          return;
        }

        if (this.localStartDate) {

          // Open current selected month
          this.monthDate = this.localStartDate;
        } else {
          this.monthDate = new Date();
        }
        this.isCalendarOpen = true;
      },
      closePicker () {
        this.isCalendarOpen = false;
        this.clickedApply();
      },
      clickedApply () {
        this.$emit('input', {
          startDate: this.localStartDate,
          endDate: this.localEndDate,
          isPickerOpened: this.isCalendarOpen,
          rangeKey: this.activeRangeKey
        });
      },
      clear () {
        if (this.isCalendarOpen) {
          this.localStartDate = null;
          this.localEndDate = null;
          this.activeRangeKey = null;
        }

        if (this.setDefaultDateAfterClear) {
          this.localStartDate = momentWrapper().toDate();
          this.localEndDate = momentWrapper().toDate();
        }
      },

      clickRange ([rangeKey]) {
        if (!rangeKey) {
          return;
        }
        const [startDate, endDate] = this.ranges[rangeKey];
        this.localStartDate = startDate;
        this.localEndDate = endDate;
        this.activeRangeKey = rangeKey;
        this.monthDate = startDate;

        if (this.autoApply) {
          this.clickedApply();
        }
      }
    }
  };

</script>

<style>
  .popper.date-range-picker__popper {
    z-index: 1200;
    margin-top: 3px;
  }
</style>
